import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
type MenuItem = {
  name: string;
  description: string;
  price: string;
  image?: string;
};
type MenuCategory = {
  id: string;
  title: string;
  items: MenuItem[];
};
const CATEGORIES: MenuCategory[] = [{
  id: 'cocktails',
  title: 'Signature Cocktails',
  items: [{
    name: 'Velvet Noir',
    description: 'Aged rum, blackberry, vanilla, citrus bitters.',
    price: '₦8,500',
    image: 'https://images.unsplash.com/photo-1536935338788-846bb9981813?auto=format&fit=crop&w=600&q=80'
  }, {
    name: 'Magenta Sour',
    description: 'Gin, hibiscus, lime, egg white foam.',
    price: '₦8,000',
    image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?auto=format&fit=crop&w=600&q=80'
  }, {
    name: 'Smoked Old Fashioned',
    description: 'Bourbon, demerara, oak smoke, orange peel.',
    price: '₦9,500',
    image: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&w=600&q=80'
  }]
}, {
  id: 'mocktails',
  title: 'Mocktails',
  items: [{
    name: 'Citrus Glow',
    description: 'Fresh orange, ginger, sparkling tonic.',
    price: '₦4,500',
    image: 'https://cdn.magicpatterns.com/uploads/dC8EbMfKPoGoiaw4vYBSmB/pexels-change-c-c-974768353-21517336.jpg'
  }, {
    name: 'Berry Reset',
    description: 'Mixed berries, mint, soda, lime.',
    price: '₦4,500',
    image: 'https://cdn.magicpatterns.com/uploads/fC9vGLBYr7toZ7GsSmaxAT/pexels-alleksana-4051754.jpg'
  }, {
    name: 'Tropic Calm',
    description: 'Pineapple, coconut, passionfruit.',
    price: '₦5,000',
    image: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?auto=format&fit=crop&w=600&q=80'
  }]
}, {
  id: 'chops',
  title: 'Gourmet Small Chops',
  items: [{
    name: 'Peppered Gizzard',
    description: 'Slow-cooked, chili glaze, scallion.',
    price: '₦6,500',
    image: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?auto=format&fit=crop&w=600&q=80'
  }, {
    name: 'Spring Rolls & Samosa',
    description: 'Crisp pastry, sweet chili dip.',
    price: '₦5,500',
    image: 'https://cdn.magicpatterns.com/uploads/8ZMvod4wTYfHaEYaVv67gU/pexels-asphotography-96974.jpg'
  }, {
    name: 'Asun Bites',
    description: 'Smoked goat, scotch bonnet, onion.',
    price: '₦7,000',
    image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?auto=format&fit=crop&w=600&q=80'
  }]
}];
export function MenuSection() {
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].id);
  const activeCategory = CATEGORIES.find(c => c.id === activeTab);
  return <section id="menu" className="w-full max-w-[95%] lg:max-w-[85%] mx-auto border-x border-b border-gridline mb-10 md:mb-20" data-id="element-176">
      <div className="px-8 py-16 md:px-16 md:py-24 border-b border-gridline flex flex-col md:flex-row md:items-end justify-between gap-8" data-id="element-177">
        <div data-id="element-178">
          <h2 className="text-white font-black uppercase tracking-tight text-3xl md:text-5xl" data-id="element-179">
            The Menu
          </h2>
          <p className="mt-4 max-w-xl text-white/55 text-sm md:text-base leading-relaxed" data-id="element-180">
            A curated selection of sips and nibbles. Bespoke menus available on
            request for private events.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4" data-id="element-181">
          {CATEGORIES.map(category => <button key={category.id} onClick={() => setActiveTab(category.id)} className={`px-6 py-3 text-xs md:text-sm font-bold uppercase tracking-widest transition-colors border ${activeTab === category.id ? 'bg-accent border-accent text-black' : 'bg-transparent border-white/20 text-white/70 hover:border-accent hover:text-accent'}`} data-id="element-182">
              {category.title}
            </button>)}
        </div>
      </div>

      <div className="p-8 md:p-16 bg-black/20" data-id="element-183">
        <AnimatePresence mode="wait" data-id="element-184">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12" data-id="element-185">
            {activeCategory?.items.map((item, i) => <motion.div key={item.name} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="group flex flex-col" data-id="element-186">
                {item.image && <div className="relative aspect-[4/3] w-full overflow-hidden mb-6 border border-white/10" data-id="element-187">
                    <img src={item.image} alt={item.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-188" />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" data-id="element-189" />
                  </div>}
                <div className="flex items-baseline justify-between gap-4 mb-2" data-id="element-190">
                  <h3 className="text-white font-bold uppercase tracking-tight text-lg md:text-xl group-hover:text-accent transition-colors" data-id="element-191">
                    {item.name}
                  </h3>
                  <span className="text-accent font-bold text-sm md:text-base whitespace-nowrap" data-id="element-192">
                    {item.price}
                  </span>
                </div>
                <div className="h-px w-full bg-gridline mb-4" data-id="element-193" />
                <p className="text-white/60 text-sm leading-relaxed" data-id="element-194">
                  {item.description}
                </p>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}